import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import basicAuthenticationConfig from "../../utils/basicAuthenticationConfig";
import InputGroup from "../elements/inputGroup/InputGroup";
import LoginGoogleForm from "../login/LoginGoogleForm";
import LoginTwoFaForm from "../login/LoginTwoFaForm";
import { closableNotification } from "../elements/notification/ClosableNotification";
import { AppContext } from "../../App";
import { useHistory } from "react-router-dom";
import { authenticationRequest } from "../../utils/authenticationRequest";
import { regex, responseStatus } from "../../utils/consts";
import { parseAxiosError } from "../../utils/response";
import LoadButton from "../elements/spinner/ButtonSpinner";
import LoginTelegramForm from "../login/LoginTelegramForm";
import LoginNicknameForm from "../login/LoginNicknameForm";
import AlertMessage from "../elements/alert/Alert";
import http from "../../http";
import { showModal } from "../../utils/consts";

import {
  StyledAuthForm,
  StyledAuthWithSocialWrapper,
  StyledOrAuthBlock,
  StyledOrAuthLine,
  StyledOrAuthText
} from "../login/styledLogin";
import { StyledButton } from "../styles/styledButton";
import ReCaptcha from "../elements/reCaptcha/ReCaptcha";
import { StyledModalLoader } from "../elements/payment/styledPaymentInvoice";
import loadingGif from "../../assets/images/loading.gif";
import {GoogleOAuthProvider} from "@react-oauth/google";

const RegistrationContainer = ({ setVisible }) => {
  const { t } = useTranslation("header");
  const { t: tE } = useTranslation("errors");

  const [existUserNickname, setExistUserNickname] = useState(false);

  const history = useHistory();

  const [errors, setErrors] = useState(null);
  const [existUser, setExistUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientDetails, setClientDetails] = useState({ email: "", password: "", retypedPassword: "" });

  const [authData, setAuthData] = useState({
    headers: null,
    body: null
  });

  const { setAuthenticated, authenticated } = useContext(AppContext);
  const recaptchaRef = useRef(null);

  const [formValues, setFormValues] = useState({ email: '', password: '', retypedPassword: '' });
  const [isValid, setIsValid] = useState(false);

  const attemptsCounterRef = useRef(0);

  useEffect(() => {
    authenticationRequest(authData, setAuthenticated, history, setExistUser, setLoading, setVisible, setExistUserNickname, tE);
  }, [authData]);

  const handleSubmit = (event, passwordHint = null) => {
    event.preventDefault();
    setLoading(true);

    const captchaToken = recaptchaRef.current.getValue();
    recaptchaRef.current.reset();

    createUser(captchaToken);
  };

  const handleSubmitNoPassword = () => {
    setLoading(true);

    const captchaToken = recaptchaRef.current.getValue();
    recaptchaRef.current.reset();

    createUser(captchaToken, true);
  };

  const login = (captchaToken) => {
    setAuthData({
      ...authData,
      body: {
        captchaToken
      },
      headers: basicAuthenticationConfig(clientDetails?.email, clientDetails?.password)
    });
  };

  const createUser = (captchaToken, noPassword = false) => {

    let autoPass = noPassword

    http.post(`/api/users`, { ...clientDetails, captchaToken, autoPass }).then(response => {
      if (response.status === responseStatus.HTTP_CREATED) {
        if(!noPassword){
          login(captchaToken);
        }else{
          setVisible(showModal.LOGIN)
        }
        closableNotification(t("successfullyRegistered"), "success");
        setClientDetails({ email: "", password: "", retypedPassword: "" });
      }
    }).catch(error => {
      const newErrors = parseAxiosError(error.response);
      console.log(newErrors)
      for (const key of Object.keys(newErrors)) {
        newErrors[key] = tE(newErrors[key]);
      }
      if ('password' in newErrors)attemptsCounterRef.current++
      setErrors(newErrors);
      if (error.response.status === responseStatus.HTTP_BAD_REQUEST) {

        setErrors((prevState) => ({
          ...prevState,
          [Object.keys(error.response.data)]: Object.values(error.response.data)
        }));
        closableNotification(error.response.data.error, "error");
        return null;
      }
    }).finally(() => setLoading(false));
  };

  const validateForm = (values) => {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email);
    const passwordValid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/.test(values.password);
    const passwordEqual = values.password === values.retypedPassword
    return emailValid && passwordValid && passwordEqual;
  };

  const onChangeInput = (event) => {
    const { name, value } = event.target;

    setClientDetails((prevState) => ({ ...prevState, [name]: value.trim() }));

    setErrors((prevState) => ({
      ...prevState,
      [name]: null
    }));

    const newFormValues = {
      ...formValues,
      [name]: value
    };

    setFormValues(newFormValues);
    setIsValid(validateForm(newFormValues));
  };

  useEffect(() => {
    if (authenticated && existUserNickname) {
      setVisible(false);
    }
  }, [authenticated, existUserNickname, setVisible]);

  const renderTwoFaOrNicknameForm = () => {
    if (loading) {
      return <StyledModalLoader minHeight={"136px"}>
        <img src={loadingGif} alt={"loading..."} />
      </StyledModalLoader>;
    }

    if (!existUserNickname) {
      return <LoginNicknameForm
        loading={loading}
        authData={authData}
        setAuthData={setAuthData}
      />;
    }

    return <LoginTwoFaForm
      loading={loading}
      authData={authData}
      setAuthData={setAuthData}
    />;
  };


  return (
    !existUser ?
      <React.Fragment>
        <StyledAuthForm>
          <form onSubmit={handleSubmit}>
            <InputGroup
              id="email"
              type="email"
              label={t("enterEmail")}
              name="email"
              autoComplete="off"
              data-regex={regex.EMAIL}
              required
              error={errors?.["email"]}
              onChange={onChangeInput}
              debounceTimeout={500}
              value={clientDetails.email}
            />
            <InputGroup
              id="password"
              label={t("enterPassword")}
              type="password"
              name="password"
              value={clientDetails.password}
              onChange={onChangeInput}
              error={errors?.password}
              required
            />
            {attemptsCounterRef.current>1?
                <div className="passwordHint" onClick={handleSubmitNoPassword}>
                  {t('passwordHint')}
                </div>
                :null
            }
            <InputGroup
              id="retypePassword"
              label={t("repeatPassword")}
              type="password"
              name="retypedPassword"
              value={clientDetails.retypedPassword}
              onChange={onChangeInput}
              error={errors?.retypedPassword}
              required
            />
            <ReCaptcha recaptchaRef={recaptchaRef} />
            <div className={"bonus-info"}>
              <span>
                {t("registerBonusInformation")}
              </span>
            </div>
            <div className="auth-action">
              {!loading ?
                <StyledButton
                  color="neutral"
                  type="submit"
                  className={isValid ? 'submit-highlight' : ''}
                >
                  {t("registration")}
                </StyledButton> :
                <LoadButton
                  color="neutral"
                  text={t("registration")}
                />}
            </div>
            <p className={'login-transfer'} onClick={()=>setVisible(showModal.LOGIN)}>{t('loginTransfer')}</p>
          </form>
        </StyledAuthForm>
        <StyledOrAuthBlock>
          <StyledOrAuthLine/>
          <StyledOrAuthText>{t("orUseSocial")}</StyledOrAuthText>
          <StyledOrAuthLine/>
        </StyledOrAuthBlock>
        <StyledAuthWithSocialWrapper>
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
          <LoginGoogleForm
            authData={authData}
            setAuthData={setAuthData}
          />
          </GoogleOAuthProvider>
          <LoginTelegramForm
            authData={authData}
            setAuthData={setAuthData}
          />
        </StyledAuthWithSocialWrapper>
      </React.Fragment> :
      renderTwoFaOrNicknameForm()
  );
};

export default RegistrationContainer;